import { isMp4File } from "@/lib/utils";
import { Attachment } from "@/types/fb-type";
import { ImageLightbox } from "@/components/ui/image-lightbox";
import { VoiceMessagePlayer } from "./VoiceMessagePlayer";
import { Loader2 } from "lucide-react";
import { useChat } from "@/contexts/ChatContext";
import { useEffect, useRef, useState } from "react";
import { resolveAttachmentUrl } from "@/services/chat-message-normalizer";

export interface AttachmentInput {
    attachment: Attachment;
    userId?: string;
    isCustomer?: boolean;
}

export function AttachmentView({ att }: { att: AttachmentInput }) {
    const { refreshMessages } = useChat();
    const missingUrlRetry = useRef({ id: '', attempts: 0 });
    const attachment = att.attachment;
    const rawAtt = attachment as any;
    const mimeType = (attachment.mimeType || rawAtt.mime_type || "").toLowerCase();
    const type = (rawAtt.type || "").toLowerCase();

    // Check all possible places where the media URL could reside
    const resolvedUrl = resolveAttachmentUrl(rawAtt);

    const isImage =
        attachment.image === true ||
        type === "image" ||
        type === "photo" ||
        !!attachment.imageData?.url ||
        !!rawAtt.image_data?.url ||
        mimeType.startsWith("image/") ||
        /\.(heic|heif|jpg|jpeg|png|webp|gif|svg)($|\?)/i.test(resolvedUrl);

    // Facebook returns voice clips as .mp4 files (often tagged video/mp4) named "audioclip-*".
    const isVoiceClip = /audioclip|voice[-_]/i.test(`${rawAtt.name || ""} ${resolvedUrl}`);

    const isAudio =
        !isImage &&
        (isVoiceClip ||
            attachment.audio === true ||
            type === "audio" ||
            type === "voice" ||
            !!rawAtt.audioData ||
            !!rawAtt.audio_data ||
            mimeType.startsWith("audio/") ||
            (isMp4File(resolvedUrl) && !attachment.video && type !== "video") ||
            /\.(mp3|wav|webm|ogg|m4a|aac|opus)($|\?)/i.test(resolvedUrl));

    const isVideo =
        !isImage &&
        !isAudio &&
        (attachment.video === true ||
            type === "video" ||
            !!rawAtt.videoData ||
            !!rawAtt.video_data ||
            mimeType.startsWith("video/") ||
            /\.(mp4|mov|avi|wmv|flv|mkv|webm)($|\?)/i.test(resolvedUrl));

    useEffect(() => {
        if (!isImage || resolvedUrl) return;
        if (missingUrlRetry.current.id !== attachment.id) {
            missingUrlRetry.current = { id: attachment.id, attempts: 0 };
        }
        let cancelled = false;
        let timer: ReturnType<typeof setTimeout>;
        const delays = [1500, 11000, 15000];
        const schedule = () => {
            const attempt = missingUrlRetry.current.attempts;
            if (cancelled || attempt >= delays.length) return;
            timer = setTimeout(async () => {
                missingUrlRetry.current.attempts += 1;
                try { await refreshMessages(true, true); }
                finally { schedule(); }
            }, delays[attempt]);
        };
        schedule();
        return () => { cancelled = true; clearTimeout(timer); };
    }, [attachment.id, isImage, resolvedUrl, refreshMessages]);

    // -------- Image with Dialog (Lightbox) --------
    if (isImage) {
        if (resolvedUrl) {
            const fullUrl = resolvedUrl;
            const thumbUrl =
                attachment.imageData?.previewUrl ||
                rawAtt.image_data?.preview_url ||
                fullUrl;
            const alt = attachment.imageData?.renderAsSticker ? "Sticker" : "Photo";
            return <ImageAttachment
                key={attachment.id || fullUrl}
                src={thumbUrl}
                fullUrl={fullUrl}
                alt={alt}
                refreshMessages={refreshMessages}
            />;
        }

        // Image attachment exists but URL is still processing/indexing on Facebook CDN
        return (
            <div className="flex items-center gap-2 py-3 px-4 rounded-xl bg-black/5 dark:bg-white/5 border border-dashed border-gray-300 text-xs text-gray-500">
                <Loader2 className="w-4 h-4 animate-spin text-blue-500 shrink-0" />
                <span>Processing photo...</span>
            </div>
        );
    }

    // -------- Voice / Audio --------
    if (isAudio && resolvedUrl) {
        return (
            <VoiceMessagePlayer
                src={resolvedUrl}
                isCustomer={att.isCustomer}
                duration={rawAtt.audioData?.duration || rawAtt.audio_data?.duration}
            />
        );
    }

    // -------- Video --------
    if (isVideo && resolvedUrl) {
        return (
            <video
                controls
                preload="metadata"
                src={resolvedUrl}
                poster={attachment.imageData?.previewUrl || rawAtt.image_data?.preview_url}
                className="rounded-lg my-2 max-w-full"
            />
        );
    }

    // -------- Fallback: link --------
    if (resolvedUrl) {
        return (
            <a
                href={resolvedUrl}
                target="_blank"
                rel="noreferrer"
                className={`underline break-all text-xs font-medium inline-flex items-center gap-1 ${
                    att.isCustomer ? "text-blue-600 hover:text-blue-800" : "text-white/90 hover:text-white"
                }`}
            >
                📎 {rawAtt.name || attachment.mimeType || "Download attachment"}
            </a>
        );
    }

    return null;
}

function ImageAttachment({
    src,
    fullUrl,
    alt,
    refreshMessages,
}: {
    src: string;
    fullUrl: string;
    alt: string;
    refreshMessages: (refreshMedia?: boolean, dedupeMediaRefresh?: boolean) => Promise<void>;
}) {
    const [imageSrc, setImageSrc] = useState(src);
    const [refreshRequested, setRefreshRequested] = useState(false);
    const hasLoadedRef = useRef(false);

    useEffect(() => {
        setImageSrc((prev) => {
            // Keep the already-loaded image element when a background history refresh
            // only updates the Facebook CDN signature on the same attachment.
            if (hasLoadedRef.current && prev && !prev.startsWith("blob:")) {
                return prev;
            }
            return src;
        });
        setRefreshRequested(false);
    }, [src, fullUrl]);

    return (
        <ImageLightbox src={fullUrl} alt={alt}>
            {({ onClick }) => (
                <button
                    type="button"
                    onClick={onClick}
                    className="group relative block max-w-full text-left focus:outline-none"
                    aria-label="View photo"
                >
                    <img
                        src={imageSrc}
                        alt={alt}
                        className="max-h-56 sm:max-h-72 w-auto max-w-full rounded-xl object-cover cursor-pointer hover:brightness-95 transition shadow-sm"
                        onLoad={() => {
                            hasLoadedRef.current = true;
                        }}
                        onError={() => {
                            hasLoadedRef.current = false;
                            if (imageSrc !== fullUrl) {
                                setImageSrc(fullUrl);
                                return;
                            }
                            if (!refreshRequested) {
                                setRefreshRequested(true);
                                void refreshMessages(true, true);
                            }
                        }}
                    />
                    <span className="absolute inset-0 rounded-xl ring-0 ring-blue-300/0 group-hover:ring-2 group-hover:ring-blue-300/40 transition pointer-events-none" />
                </button>
            )}
        </ImageLightbox>
    );
}
